<template>
  <label
    class="sc3-checkbox"
    :for="id"
    :class="{
      'dark-font': isDarkFont,
      'small-margin': isLabelCloseToIcon,
      'small-font': isSmallFont,
      'large-font': isLargeFont,
      'orange': isOrange,
      'star': isStar,
      'single-spaced': isSingleSpaced,
      'double-spaced': isDoubleSpaced,
      'sc3-inline-height': isSc3InlineHeight,
      'middle-aligned': isMiddleAligned,
      'disabled': isDisabled,
      'bold': isBold,
      'label-text-nowrap': preventLabelTextFromWrappingOnWhitespace,
      'descriptive': !!description,
      'lato-font': isLatoFont,
      'roboto-font': isRobotoFont
    }"
    :style="{ justifyContent: justify }"
  >
    <input
      type="checkbox"
      :id="id"
      :checked="checkboxValue"
      @change="handleCheckboxValueChanged($event)"
      :disabled="isDisabled"
    />
    <i class="fas fa-fw fa-check-circle" v-if="checkboxValue && isCircle"></i>
    <i class="far fa-fw fa-circle" v-if="!checkboxValue && isCircle"></i>
    <i class="fas fa-fw fa-check-square" v-if="checkboxValue && isSquare"></i>
    <i class="far fa-fw fa-square" v-if="!checkboxValue && isSquare"></i>
    <i class="fas fa-fw fa-star" v-if="checkboxValue && isStar"></i>
    <i class="far fa-fw fa-star" v-if="!checkboxValue && isStar"></i>
    <i class="far fa-fw fa-toggle-on" v-if="checkboxValue && isToggleSlider"></i>
    <i class="far fa-fw fa-toggle-off" v-if="!checkboxValue && isToggleSlider"></i>
    <span
      v-if="labelText"
      class="label-text-container"
      :class="{ 'label-text-container-broken-words': lineBreakEveryWord, 'label-text-container-inline': !lineBreakEveryWord }"
    >
      <template v-if="lineBreakEveryWord">
        <span
          v-for="(labelTextWord, labelTextWordIndex) in labelTextWordArray"
          :key="`label-text-word-${labelTextWordIndex}`"
        ><br v-if="labelTextWordIndex > 0" />{{ labelTextWord }}</span>
      </template>
      <span v-else class="title">{{ labelText }}</span>
      <br v-if="description">
      <span v-if="description" class="description">
        {{ description }}
      </span>
    </span>
  </label>
</template>

<script lang="ts" setup>
  import { toRefs, computed } from 'vue';

  const props = withDefaults(defineProps<{
    id: string;
    checkboxValue: boolean;
    labelText?: string;
    description?: string;
    iconSize?: number;
    titleFontSize?: number;
    descriptionFontSize?: number;
    spaceBetweenTextAndIcon?: number;
    lineBreakEveryWord?: boolean;
    isDarkFont?: boolean;
    isSmallFont?: boolean;
    isLargeFont?: boolean;
    isLabelCloseToIcon?: boolean;
    isOrange?: boolean;
    isCircle?: boolean;
    isSquare?: boolean;
    isToggleSlider?: boolean;
    isStar?: boolean;
    isSingleSpaced?: boolean;
    isDoubleSpaced?: boolean;
    isSc3InlineHeight?: boolean;
    isMiddleAligned?: boolean;
    isBold?: boolean;
    preventLabelTextFromWrappingOnWhitespace? :boolean;
    isDisabled?: boolean;
    isLatoFont?: boolean;
    isRobotoFont?: boolean;
    justify?: 'start' | 'end' | 'center' | 'space-around' | 'space-between' | 'space-evenly';
  }>(), {
    iconSize: 14,
    titleFontSize: 14,
    descriptionFontSize: 14,
    spaceBetweenTextAndIcon: 4,
    lineBreakEveryWord: false,
    isDarkFont: false,
    isSmallFont: false,
    isLargeFont: false,
    isLabelCloseToIcon: false,
    isOrange: false,
    isCircle: false,
    isSquare: false,
    isToggleSlider: false,
    isStar: false,
    isSingleSpaced: false,
    isDoubleSpaced: false,
    isSc3InlineHeight: false,
    isMiddleAligned: false,
    isBold: false,
    isDisabled: false,
    isLatoFont: false,
    isRobotoFont: false
  });

  const {
    id,
    checkboxValue,
    labelText,
    description,
    lineBreakEveryWord,
    isDarkFont,
    isSmallFont,
    isLargeFont,
    isLabelCloseToIcon,
    isOrange,
    isCircle,
    isSquare,
    isToggleSlider,
    isStar,
    isSingleSpaced,
    isDoubleSpaced,
    isSc3InlineHeight,
    isMiddleAligned,
    isBold,
    isDisabled,
    isLatoFont,
    isRobotoFont,
    justify
  } = toRefs(props);

  const emit = defineEmits<{
    (e: 'update:checkboxValue', checkboxValue: boolean): void;
  }>();
  const labelTextWordArray = computed(() => {
    if (!labelText || !labelText.value) {
      return [];
    }

    return labelText.value.split(/\s/).filter(labelTextPart => !!labelTextPart);
  });

  function handleCheckboxValueChanged(e: Event) {
    if (isDisabled.value || !(e.target instanceof HTMLInputElement)) {
      return;
    }

    const isChecked = e.target.checked;
    emit('update:checkboxValue', isChecked);
  }
</script>

<style scoped>
  * {
    --descriptive-title-font-size: 18px;
    --label-line-height: 1.5;
  }

  .descriptive .title {
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
    color: #4f5964 !important;
    font-size: var(--descriptive-title-font-size) !important;
  }

  label.descriptive:hover .title {
    color: #2d3339 !important;
  }

  .descriptive i {
    align-self: start;
    line-height: calc(var(--descriptive-title-font-size) * var(--label-line-height));
  }

  .label-text-nowrap .title {
    white-space: nowrap;
  }

  label.sc3-checkbox {
    display: flex;
    align-items: center;
    width: 100%;
    cursor: pointer;
    font-weight: 700;
    color: #707f8f;
    transition: all 0.35s ease;
    font-size: 0px;
  }

  label.sc3-checkbox.disabled {
    cursor: not-allowed;
  }

  label.sc3-checkbox:not(.disabled):hover {
    color: #2d3339;
  }

  label.sc3-checkbox.dark-font {
    color: #2d3339;
  }

  label.sc3-checkbox:not(.disabled).dark-font:hover {
    color: black;
  }

  label.sc3-checkbox i {
    color: #0f4d8a;
    display: inline-block;
    font-size: v-bind('props.iconSize + "px"');
  }

  label.sc3-checkbox input[type=checkbox] {
    display: none;
  }

  label.sc3-checkbox span.label-text-container {
    display: inline-block;
    margin: 0px 0px 0px v-bind('props.spaceBetweenTextAndIcon + "px"');
    font-size: v-bind('props.titleFontSize + "px"');
    user-select: none;
    line-height: var(--label-line-height);
  }

  label.sc3-checkbox span.label-text-container.label-text-container-broken-words,
  label.sc3-checkbox.small-margin span.label-text-container {
    margin-left: 4px;
  }

  label.sc3-checkbox.small-font i,
  label.sc3-checkbox.small-font span.label-text-container {
    font-size: 12px;
  }

  label.sc3-checkbox.large-font i,
  label.sc3-checkbox.large-font span.label-text-container {
    font-size: 16px;
  }

  label.sc3-checkbox.orange i {
    color: #fa8238;
  }

  label.sc3-checkbox.star i {
    font-size: 20px;
  }

  label.sc3-checkbox span.label-text-container.label-text-container-inline {
    max-width: calc(100% - 12.25px - 8px); /* 12.25px is the width of the checkbox icon and 8px is the margin-left */
  }

  label.sc3-checkbox.small-margin span.label-text-container.label-text-container-inline {
    max-width: calc(100% - 12.25px - 4px); /* 12.25px is the width of the checkbox icon and 4px is the margin-left */
  }

  label.sc3-checkbox.small-font span.label-text-container.label-text-container-inline {
    max-width: calc(100% - 10.5px - 8px); /* 10.5px is the width of the checkbox icon and 8px is the margin-left */
  }

  label.sc3-checkbox.small-font.small-margin span.label-text-container.label-text-container-inline {
    max-width: calc(100% - 10.5px - 4px); /* 10.5px is the width of the checkbox icon and 4px is the margin-left */
  }

  label.sc3-checkbox.large-font span.label-text-container.label-text-container-inline {
    max-width: calc(100% - 14px - 8px); /* 14px is the width of the checkbox icon and 8px is the margin-left */
  }

  label.sc3-checkbox.large-font.small-margin span.label-text-container.label-text-container-inline {
    max-width: calc(100% - 14px - 4px); /* 14px is the width of the checkbox icon and 4px is the margin-left */
  }

  label span.description {
    color: #707f8f;
    font-weight: normal;
    font-size: v-bind('props.descriptionFontSize + "px"');
  }

  .bold .label-text-container {
    font-weight: bold;
  }

  .lato-font .label-text-container {
    font-family: 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }

  .roboto-font .label-text-container {
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }
</style>
